import {
  Bell,
  CalendarDays,
  CircleHelp,
  Clock,
  CreditCard,
  Package,
  PhoneCall,
  Plus,
  Search,
  Wrench,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import ThemeToggle from '../shared/ThemeToggle';
import { useLocalization } from '../../hooks/useLocalization';

const closedStatuses = ['finalizada', 'entregue', 'cancelada'];

async function getOficinaId() {
  const { data: authData } = await supabase.auth.getUser();
  const email = authData?.user?.email;
  if (!email) return null;

  const { data: userRow } = await supabase
    .from('usuarios')
    .select('oficina_id')
    .eq('email', email)
    .maybeSingle();

  return userRow?.oficina_id || null;
}

function safeTerm(value) {
  return value.trim().replace(/[%,]/g, '');
}

export default function Header({
  title = 'Dashboard',
  variant = 'client',
  onOpenOnboarding,
}) {
  const { settings, t } = useLocalization();
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const searchRef = useRef(null);
  const notifRef = useRef(null);

  const now = useMemo(
    () => new Date().toLocaleDateString(settings.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: settings.timeZone,
    }),
    [settings.locale, settings.timeZone],
  );

  useEffect(() => {
    function handleClick(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) setSearchOpen(false);
      if (notifRef.current && !notifRef.current.contains(event.target)) setNotifOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    async function loadNotifs() {
      try {
        const oficinaId = await getOficinaId();
        if (!oficinaId) return;

        const today = new Date().toISOString().split('T')[0];
        const lista = [];

        const [ordensRes, estoqueRes, agendaRes, pagamentoRes, lembretesRes] = await Promise.all([
          supabase
            .from('ordens_servico')
            .select('id, numero, status, data_previsao')
            .eq('oficina_id', oficinaId),
          supabase
            .from('estoque')
            .select('id, nome, quantidade, estoque_minimo')
            .eq('oficina_id', oficinaId),
          supabase
            .from('agendamentos')
            .select('id')
            .eq('oficina_id', oficinaId)
            .gte('data_hora', `${today}T00:00:00`)
            .lte('data_hora', `${today}T23:59:59`),
          supabase
            .from('assinaturas')
            .select('status_pagamento, data_vencimento')
            .eq('oficina_id', oficinaId)
            .maybeSingle(),
          supabase
            .from('ordens_servico')
            .select('id, numero, lembrete_contato_em, lembrete_contato_obs, clientes(nome, telefone)')
            .eq('oficina_id', oficinaId)
            .not('lembrete_contato_em', 'is', null)
            .is('lembrete_contato_concluido_em', null)
            .lte('lembrete_contato_em', today)
            .order('lembrete_contato_em', { ascending: true })
            .limit(5),
        ]);

        const ordens = ordensRes.data || [];
        const atrasadas = ordens.filter((ordem) => {
          const status = String(ordem.status || '').toLowerCase();
          const vencida = ordem.data_previsao && ordem.data_previsao < today;
          return status === 'atrasada' || (vencida && !closedStatuses.includes(status));
        });

        if (atrasadas.length > 0) {
          lista.push({
            id: 'os-atrasadas',
            icon: Clock,
            tone: 'text-red-400',
            message: `${atrasadas.length} OS atrasada${atrasadas.length > 1 ? 's' : ''}`,
            link: '/ordens-servico',
          });
        }

        const estoqueBaixo = (estoqueRes.data || []).filter((item) => {
          const quantidade = Number(item.quantidade || 0);
          const minimo = Number(item.estoque_minimo || 0);
          return minimo > 0 && quantidade <= minimo;
        });

        if (estoqueBaixo.length > 0) {
          lista.push({
            id: 'estoque-baixo',
            icon: Package,
            tone: 'text-yellow-400',
            message: `${estoqueBaixo.length} item${estoqueBaixo.length > 1 ? 'ns' : ''} perto de esgotar no estoque`,
            link: '/estoque',
          });
        }

        const agendaHoje = agendaRes.data || [];
        if (agendaHoje.length > 0) {
          lista.push({
            id: 'agenda-hoje',
            icon: CalendarDays,
            tone: 'text-blue-400',
            message: `${agendaHoje.length} agendamento${agendaHoje.length > 1 ? 's' : ''} para hoje`,
            link: '/agenda',
          });
        }

        const lembretesContato = lembretesRes.data || [];
        lembretesContato.forEach((lembrete) => {
          lista.push({
            id: `lembrete-contato-${lembrete.id}`,
            icon: PhoneCall,
            tone: 'text-green-400',
            message: `Contato pós-revisão: ${lembrete.clientes?.nome || `OS ${lembrete.numero}`}`,
            link: `/ordens/${lembrete.id}`,
          });
        });

        const assinatura = pagamentoRes.data;
        const statusPagamento = String(assinatura?.status_pagamento || '').toLowerCase();
        const vencimento = assinatura?.data_vencimento;
        const vencido = vencimento && vencimento < today;
        const vencimentoDia = vencimento ? String(vencimento).split('T')[0] : null;
        const diasParaVencer = vencimentoDia
          ? Math.ceil((new Date(`${vencimentoDia}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000)
          : null;
        if (['pendente', 'atrasado', 'em aberto'].includes(statusPagamento) || vencido) {
          lista.push({
            id: 'pagamento-pendente',
            icon: CreditCard,
            tone: vencido || statusPagamento === 'atrasado' ? 'text-red-400' : 'text-orange-400',
            message: vencido || statusPagamento === 'atrasado' ? 'Mensalidade vencida' : 'Mensalidade pendente',
            link: '/configuracoes/pagamento',
          });
        } else if (diasParaVencer !== null && diasParaVencer >= 0 && diasParaVencer <= 7) {
          lista.push({
            id: 'pagamento-vencendo',
            icon: CreditCard,
            tone: 'text-orange-400',
            message: `Mensalidade vence em ${diasParaVencer} dia${diasParaVencer === 1 ? '' : 's'}`,
            link: '/configuracoes/pagamento',
          });
        }

        setNotifs(lista);
      } catch (error) {
        console.error('Erro ao carregar notificações:', error);
      }
    }

    loadNotifs();
    window.addEventListener('focus', loadNotifs);
    const timer = window.setInterval(loadNotifs, 5 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', loadNotifs);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const term = safeTerm(searchTerm);
    if (!term) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const oficinaId = await getOficinaId();
        if (!oficinaId) return;

        const [clientesRes, veiculosRes] = await Promise.all([
          supabase
            .from('clientes')
            .select('id, nome, telefone')
            .eq('oficina_id', oficinaId)
            .is('removed_at', null)
            .or(`nome.ilike.%${term}%,telefone.ilike.%${term}%`)
            .limit(6),
          supabase
            .from('veiculos')
            .select('id, placa, modelo, marca')
            .eq('oficina_id', oficinaId)
            .is('removed_at', null)
            .or(`placa.ilike.%${term}%,modelo.ilike.%${term}%`)
            .limit(6),
        ]);

        const clientes = clientesRes.data || [];
        const clienteIds = clientes.map((cliente) => cliente.id);

        const osPorNumero = await supabase
          .from('ordens_servico')
          .select('id, numero, status, cliente_id, clientes(nome)')
          .eq('oficina_id', oficinaId)
          .ilike('numero', `%${term}%`)
          .limit(6);

        let osPorCliente = { data: [] };
        if (clienteIds.length > 0) {
          osPorCliente = await supabase
            .from('ordens_servico')
            .select('id, numero, status, cliente_id, clientes(nome)')
            .eq('oficina_id', oficinaId)
            .in('cliente_id', clienteIds)
            .limit(6);
        }

        const osMap = new Map();
        [...(osPorNumero.data || []), ...(osPorCliente.data || [])].forEach((ordem) => {
          osMap.set(ordem.id, ordem);
        });

        setSearchResults([
          ...clientes.map((cliente) => ({
            type: 'Cliente',
            label: cliente.nome,
            description: cliente.telefone || 'Sem telefone',
            link: '/clientes',
          })),
          ...(veiculosRes.data || []).map((veiculo) => ({
            type: 'Veículo',
            label: veiculo.placa || veiculo.modelo,
            description: [veiculo.marca, veiculo.modelo].filter(Boolean).join(' '),
            link: '/veiculos',
          })),
          ...Array.from(osMap.values()).map((ordem) => ({
            type: 'OS',
            label: ordem.numero,
            description: `${ordem.clientes?.nome || 'Cliente não informado'} · ${ordem.status || 'sem status'}`,
            link: '/ordens-servico',
          })),
        ].slice(0, 12));
      } catch (error) {
        console.error('Erro na busca global:', error);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <header className="relative z-30 flex min-h-16 flex-wrap items-center gap-3 border-b border-border bg-bg px-3 py-3 sm:flex-nowrap sm:gap-4 sm:px-4 lg:px-6">
      <div className="min-w-0 flex-1">
        <div className="font-display text-lg font-bold capitalize text-white">{title}</div>
        <div className="hidden items-center gap-1.5 text-xs text-zinc-500 sm:flex">
          <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
          {now} · {t('live')}
        </div>
      </div>

      <div className="flex min-w-0 flex-shrink-0 items-center gap-2">
        <div ref={searchRef} className="relative">
          {searchOpen ? (
            <div className="flex h-9 items-center gap-2 rounded-lg border border-border bg-surface2 px-3">
              <Search size={14} className="flex-shrink-0 text-zinc-400" />
              <input
                autoFocus
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('search')}
                className="w-[calc(100vw-9rem)] bg-transparent text-sm text-white outline-none sm:w-56"
              />
              <button type="button" onClick={closeSearch} aria-label="Fechar busca">
                <X size={14} className="text-zinc-400 hover:text-white" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.06] bg-zinc-800/50 text-zinc-400 transition-colors hover:text-white"
              aria-label="Abrir busca global"
            >
              <Search size={15} />
            </button>
          )}

          {searchOpen && searchTerm.trim() && (
            <div className="fixed left-3 right-3 top-16 max-h-[70dvh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-80">
              {searchLoading ? (
                <div className="px-4 py-5 text-center text-sm text-zinc-500">Buscando...</div>
              ) : searchResults.length > 0 ? (
                searchResults.map((result, index) => (
                  <button
                    type="button"
                    key={`${result.type}-${result.label}-${index}`}
                    onClick={() => {
                      navigate(result.link);
                      closeSearch();
                    }}
                    className="flex w-full items-center gap-3 border-b border-white/[0.04] px-4 py-3 text-left transition-colors hover:bg-zinc-800"
                  >
                    <span className="flex-shrink-0 rounded-full bg-orange-500/20 px-2 py-0.5 text-xs font-medium text-orange-400">
                      {result.type}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-white">{result.label}</div>
                      <div className="truncate text-xs text-zinc-500">{result.description}</div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-5 text-center text-sm text-zinc-500">Nenhum resultado encontrado</div>
              )}
            </div>
          )}
        </div>

        <ThemeToggle />

        {variant !== 'gdn' && onOpenOnboarding && (
          <button
            type="button"
            onClick={onOpenOnboarding}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.06] bg-zinc-800/50 text-zinc-400 transition-colors hover:text-white"
            aria-label="Rever tutorial do MecSync"
            title="Rever tutorial"
          >
            <CircleHelp size={15} />
          </button>
        )}

        <div ref={notifRef} className="relative">
          <button
            type="button"
            onClick={() => setNotifOpen((current) => !current)}
            className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.06] bg-zinc-800/50 text-zinc-400 transition-colors hover:text-white"
            aria-label="Abrir notificações"
          >
            <Bell size={15} />
            {notifs.length > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold text-black">
                {notifs.length}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="fixed left-3 right-3 top-16 max-h-[70dvh] overflow-y-auto rounded-xl border border-border bg-surface shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-80">
              <div className="border-b border-white/[0.06] px-4 py-3">
                <span className="text-sm font-semibold text-white">{t('notifications')}</span>
              </div>
              {notifs.length === 0 ? (
                <div className="px-4 py-6 text-center text-sm text-zinc-500">Nenhuma notificação</div>
              ) : (
                notifs.map((notification) => {
                  const Icon = notification.icon || Wrench;
                  return (
                    <button
                      type="button"
                      key={notification.id}
                      onClick={() => {
                        navigate(notification.link);
                        setNotifOpen(false);
                      }}
                      className="flex w-full items-center gap-3 border-b border-white/[0.04] px-4 py-3 text-left transition-colors hover:bg-zinc-800"
                    >
                      <Icon size={17} className={notification.tone} />
                      <span className={`text-sm ${notification.tone}`}>{notification.message}</span>
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>

        {variant !== 'gdn' && (
          <button
            type="button"
            onClick={() => navigate('/ordens-servico')}
            className="flex h-9 items-center gap-1.5 rounded-lg bg-orange-500 px-3 text-sm font-semibold text-black transition-colors hover:bg-orange-400"
          >
            <Plus size={15} />
            <span className="hidden sm:inline">{t('newOrder')}</span>
          </button>
        )}
      </div>
    </header>
  );
}
